<template>
  <div v-if="isVisible" class="overlay" @click="dismissPopup">
    <div class="popup" :class="popupClass">
      <button v-if="addCloseIcon" class="popup-close-btn" @click.stop="dismissPopupCloseBtn">
        <img :src="closeSmallBlackIcon" alt="Close" />
      </button>
      <img v-if="imageSrc" :src="imageSrc" alt="Popup image" class="popup-image" />
      <div class="message">
        <slot />
      </div>
      <div class="close-button-container">
        <button class="primary" :style="buttonColor ? { '--btn-bg': buttonColor } : {}" @click.stop="closePopup">
          {{ closeButtonText }}
        </button>
        <button
          v-if="bottomLinkText"
          type="button"
          class="popup-bottom-link secondary"
          :class="{ 'popup-bottom-link-highlight': bottomLinkHighlight }"
          :data-testid="bottomLinkTestId || undefined"
          @click.stop="bottomLinkHandler"
        >
          {{ bottomLinkText }}
        </button>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import { useI18n } from "vue-i18n";
import closeSmallBlackIcon from "@/assets/icons/close_small_black.svg";

export default defineComponent({
  name: "HighlightPopup",
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
    buttonText: {
      type: String,
      default: null,
    },
    buttonColor: {
      type: String,
      default: null,
    },
    bottomLinkText: {
      type: String,
      default: "",
    },
    bottomLinkHighlight: {
      type: Boolean,
      default: false,
    },
    bottomLinkTestId: {
      type: String,
      default: "",
    },
    addCloseIcon: {
      type: Boolean,
      default: false,
    },
    imageSrc: {
      type: String,
      default: "",
    },
    popupClass: {
      type: String,
      default: "",
    },
  },
  emits: ["close-popup", "dismiss-popup", "dismiss-popup-close-btn", "bottom-link-click"],
  setup(props) {
    const { t } = useI18n();

    const closeButtonText = computed(() => props.buttonText ?? t("HighLight-ButtonText"));

    return {
      t,
      closeButtonText,
      closeSmallBlackIcon,
    };
  },
  methods: {
    closePopup() {
      this.$emit("close-popup");
    },
    dismissPopup() {
      this.$emit("dismiss-popup");
    },
    dismissPopupCloseBtn() {
      this.$emit("dismiss-popup-close-btn");
    },
    bottomLinkHandler() {
      this.$emit("bottom-link-click");
    },
  },
});
</script>

<style scoped>
.overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: flex-end;
  justify-content: center;
}

.popup {
  width: 100%;
  background: white;
  border-radius: 10px 10px 0 0;
  animation: slideUp 0.3s ease-out;
  position: relative;
}

@media (min-width: 768px) {
  .popup {
    max-width: 400px;
  }
}

.close-button-container {
  flex: 1;
  display: flex;
  flex-direction: column;
  background: var(--Gray-elements-Background-BLOCKS);
  padding-bottom: 50px;
  padding-left: 0;
  padding-right: 0;
  margin: 12px;
  gap: 12px;
}

.popup-close-btn {
  position: absolute;
  top: 12px;
  right: 12px;
  width: auto;
  background: none;
  border: none;
  padding: 5px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  transition: background-color 0.2s;
}

.popup-close-btn:hover {
  background-color: rgba(0, 0, 0, 0.08);
}

.popup-image {
  display: block;
  margin: 45px auto -30px auto;
  padding: 0 22px;
}

.message {
  padding: 20px;
  text-align: center;
  margin-top: 20px;
}

@keyframes slideUp {
  from {
    transform: translateY(100%);
  }
  to {
    transform: translateY(0);
  }
}

/* PYMT-2651: "Get an extension" secondary CTA — full-width light-green pill
   instead of the default text link. Higher specificity to override the global
   `.popup-bottom-link.secondary` link styling. */
.popup-bottom-link.secondary.popup-bottom-link-highlight {
  width: 100%;
  border: none;
  border-radius: 100px;
  padding: 16px;
  font-weight: 600;
  text-decoration: none;
  background: var(--Green-elements-Background-LIGHT, #e6f4d7);
  color: var(--Green-elements-Text-DARK, #1b3a13);
  cursor: pointer;
}

button.primary[style] {
  background-color: var(--btn-bg);
}

button.primary[style]:hover {
  background-color: color-mix(in srgb, var(--btn-bg) 85%, black);
}

@media (max-width: 350px) {
  .close-button {
    min-width: 85%;
  }
}
</style>
